import React, { useEffect, useRef } from 'react';
import { useTranslation } from "react-i18next";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  hideCloseButton?: boolean;
  isCentered?: boolean;
  className?: string;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, hideCloseButton = false, isCentered = false, className = '' }) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const isSelectingText = useRef(false);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleMouseDown = () => {
    isSelectingText.current = true;
  };

  const handleMouseUp = () => {
    setTimeout(() => {
      isSelectingText.current = false;
    }, 10);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (isSelectingText.current) {
      return;
    }
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div 
        ref={modalRef}
        className={`license-modal ${isCentered ? 'center' : ''} ${className}`} 
        onClick={e => e.stopPropagation()}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        <div className="modal-header">
          <h3>{title}</h3>
          {!hideCloseButton && (
            <button 
              className="modal-close" 
              onClick={onClose}
              aria-label={t('modal_close')}
            >
              ×
            </button>
          )}
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal; 